import React from 'react';
import _ from 'lodash';

import {
  QueryCachedOrdersFilterInput,
  SalesOrdersActivity_TabBarDocument,
  useReviewCachedOrdersMutation,
  useSalesOrdersActivity_NewOrderTableLazyQuery
} from 'generated/graphql';

import { listQueryUtil } from 'iron-forge-react-modules';
import * as sharedTypes from '../types';
import * as types from './types';
import { DateTime } from 'luxon';



const useListQuery = listQueryUtil.genUseListQuery<types.IColumnId>();



export interface ICachedOrder {
  id: string;
  orderNumber: string;
  storeName: string;
  createdTimestamp: DateTime;
  validationErrors: string[];
}

export interface IData {
  cachedOrders: ICachedOrder[];
}

export function useQuery(defaultFilter: sharedTypes.IFilter) {
  return useListQuery({
    useLazyQuery: useSalesOrdersActivity_NewOrderTableLazyQuery,
    clientColumn2GqlSortField: {
      orderDate: 'createdDate'
    },
    defaultFilterArgs: defaultFilter,
    genFilter2GqlFilter: defaultFilterArgs => (filter?: sharedTypes.IFilter): QueryCachedOrdersFilterInput => ({
      salesChannel: filter?.salesChannel ?? defaultFilterArgs.salesChannel,
      storeId: filter?.storeId ?? defaultFilterArgs.storeId,
      isProcessed: false,
      orderNumber: defaultFilter.searchText || filter?.searchText,
      after: filter?.after.toISO() ?? defaultFilter.after.toISO()
    }),
    gqlResultData2GqlPageInfo: gqlData => gqlData?.cachedOrders.pageInfo,
    initQuerySort: {
      columnId: 'orderDate',
      direction: 'descending'
    },
    gqlResultData2TupleData: (gqlData): IData => ({
      cachedOrders: _.map(gqlData.data?.cachedOrders.nodes ?? [], (node): ICachedOrder => ({
        id: node.id,
        createdTimestamp: DateTime.fromISO(node.metaCreated),
        orderNumber: node.rawOrderNumber,
        validationErrors: node.validationErrors,
        storeName: node.store.name
      }))
    })
  });
}



export function useReviewCachedOrders(filter: sharedTypes.IFilter) {
  const [fnMutate, tuple] = useReviewCachedOrdersMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: SalesOrdersActivity_TabBarDocument,
        variables: {
          salesChannelName: filter.salesChannel,
          storeId: filter.storeId || undefined, // empty string if not selected, but server requires UUID string.
          searchText: filter.searchText,
          after: filter.after.toISO()
        }
      }
    ]
  });

  const fnWrappedMutate = React.useCallback(
    async (ids: string[]) => {
      await fnMutate({
        variables: {
          ids
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
